/**
 * OpenApiGeneratorで生成された、ApiClientをDIするためのプロバイダ。
 * エンドポイント、メソッドごとの関数、provideを使用して全てねじ込む。
 * この層で行うこと
 * - OpenApiGeneratorの各関数を、アプリケーションのインターフェイスに整える為にラッピングする。
 *   - （必須）エラー系についてはAPIのもたらすエラー型と、axiosモジュールが吐くエラー型を統一する。
 *   - tokenなどのデフォルトパラメタを、コンテキストから実行時に取得する。
 */
// class形式、FP、factoryの３種のモジュールが存在するが、Factoryタイプを採用する。
import {
  AtoneAuthenticateApiFactory,
  AtoneMemberApiFactory,
  AtoneZipAddressApiFactory,
  AtoneSmsApiFactory,
  AtoneJavascriptApiFactory,
  AtonePaymentApiFactory,
  AtoneTracksApiFactory,
  AtoneShopApiFactory,
  AtoneRequestTokenApiFactory,
  AtonePointApiFactory,
  AtoneFufururuApiFactory,
  ErrorResponse,
  ErrorResponseSystemTypeEnum,
  NpAtobaraiTransactionApiFactory,
  AtonePointGetResponse,
  NapoleonApiFactory,
  AtoneErrorMessage,
} from '@netprotections/berlin-bff';
import axios, { AxiosAdapter, AxiosError, AxiosInterceptorManager, AxiosRequestConfig, AxiosResponse } from 'axios';
import { createContext } from '@berlin-front/common/composables/context';
import qs from 'qs';
import { Ref } from '@vue/composition-api';
import adapter from '@axios_adapter';
import {
  camelCaseDeep,
  hasPath,
  hasProps,
  includes,
  isNum,
  isStr,
  notNil,
  snakeCaseDeep,
} from '@berlin-front/common/utils';
import { API_ERROR_TYPE, broadcast } from '@/broadcast';
import { useOrigin } from '@berlin-front/common/composables/useClientConnection';
import { z } from 'zod';
import useIsLegacy from '@composables/useIsLegacy';

const metaServiceParser = z.object({
  service_type: z.string(),
  modal_mode: z.string().optional(),
});
const getServiceFromMeta = (meta: unknown) => {
  const result = metaServiceParser.safeParse(meta);
  return result.success ? result.data : undefined;
};
const baseUrl = process.env.VUE_APP_ATONE_ENDPOINT;

const pointGetErrorDummyResponse: AxiosResponse<AtonePointGetResponse> = {
  config: {},
  data: {
    point: -1,
  },
  headers: {},
  status: 200,
  statusText: 'Ok',
};

// 定数
const SHOP_ERROR_CODE = [
  'EATN0301', // 決済認証トークンが指定されていません。
  'EATN0304', // 店舗公開可能鍵のデータが存在しません。
  'EATN0307', // 追跡トークンの形式が不正です。
  'EATN0331', // 課金額は1以上の7桁以内の半角数値で設定して下さい。
  'EATN0332', // 加盟店取引IDは100文字以内の半角英数字記号で設定して下さい。
  'EATN0333', // 売上確定はtrueまたはfalseのいずれかを設定して下さい。
  'EATN0334', // 加盟店取引備考は改行以外の制御文字を除く255文字以内で設定して下さい。
  'EATN0335', // 加盟店商品IDは255文字以内の半角英数字記号で設定して下さい。
  'EATN0336', // 商品明細に値が設定されていません。
  'EATN0337', // 商品明細数は9999以下で設定して下さい。
  'EATN0338', // 商品名は255文字以内で設定して下さい。
  'EATN0339', // 商品単価は6桁以内の半角数値で設定して下さい。
  'EATN0340', // 個数は5桁以内の半角数値で設定して下さい。
  'EATN0341', // 商品URLは1000文字以内で設定して下さい。
  'EATN0342', // 氏名は100文字以内の全角で設定してください。
  'EATN0343', // よみがな氏名は128文字以内の全角で設定してください。
  'EATN0344', // 会社名は100文字以内で設定してください。
  'EATN0345', // 部署名は30文字以内で設定してください。
  'EATN0346', // 郵便番号は半角数字7桁または半角数字3桁+"-"+半角数字4桁で設定してください。
  'EATN0347', // 住所は255文字以内で設定してください。
  'EATN0348', // 半角の電話番号形式（81、+81から始まる国際電話番号形式を含む）で入力してください。
  'EATN0349', // メールアドレスは255文字以内の正しい形式で設定してください。
  'EATN0350', // 利用ポイント数は7桁以内の半角数値で設定してください。
  'EATN0351', // 不正なチェックサムです。
  'EATN0352', // HTTPリクエストのContent-Typeにはapplication/jsonを指定して下さい。
  'EATN0353', // サービス提供先数は9以下で設定して下さい。
  'EATN0354', // 姓は50文字以内の全角で設定してください。
  'EATN0355', // 名は50文字以内の全角で設定してください。
  'EATN0356', // よみがな姓は64文字以内の全角で設定してください。
  'EATN0357', // よみがな名は64文字以内の全角で設定してください。
  'EATN0358', // 半角の携帯電話番号形式（81、+81から始まる国際携帯電話番号形式を含む）で入力してください。
  'EATN0359', // 生年月日はYYYY-MM-DD形式の日付を設定してください。
  'EATN0360', // 性別は男性:1, 女性:2で設定してください。
  'EATN0361', // 累計購入回数は0以上の7桁以内の半角数値で設定して下さい。
  'EATN0362', // 累計購入金額は0以上の8桁以内の半角数値で設定して下さい。
  'EATN0364', // ユーザーIDは255文字以下の半角文字で設定して下さい。
  'EATN0366', // 店舗会員IDは100文字以内の半角英数字記号で設定して下さい。
  'EATN0367', // 会員歴は0以上の7桁以内の半角数値で設定して下さい。
  'EATN0368', // 本人確認ステータスは1:メール認証,2:SMS認証,3:書類確認,4:SNS認証,9:未確認で設定して下さい。
  'EATN0369', // 商材カテゴリは100文字以内で設定してください。
  'EATN0370', // ブランド名は100文字以内で設定してください。
  'EATN0371', // 過去利用決済は1:カード,2:前払い,3:代引き,4:後払い,5:キャリア決済,9:初回,99:その他で設定してください。
  'EATN0372', // 端末IDは100文字以内の半角英数字記号で設定して下さい。
  'EATN0373', // 累計販売回数は0以上の7桁以内の半角数値で設定して下さい。
  'EATN0374', // 累計販売額は0以上の8桁以内の半角数値で設定して下さい。
  'EATN0396', // Grape Error
  'EATN0397', // HTTPリクエストに含まれるJSONの形式が不正です。
  'EATN1101', // 同じ加盟店取引IDが既に登録されています。
  'EATN1102', // 課金額と商品明細金額の合計が一致していません。
  'EATN1103', // 利用ポイント数が課金額を超過しています。
  'EATN1104', // 加盟店検証用以外の会員情報が指定されています。
  'EATN0400', // 取引オプションは1〜3の半角数値で設定してください。
  'EATN1004', // 定期登録オプションが設定されていません。
  'EATN1005', // 金額更新オプションが設定されていません。
  'EATN1006', // 仮登録オプションが設定されていません。
  'EATN1011', // 仮登録の場合は売上確定できません。
  'EATN0154', // 不正なターミナルIDです。
  'EATN0365', // 有効なサービス利用契約が無いため処理できません。
  'EATN0440', // 店舗公開可能鍵は有効ではありません。
  'EATN0442', // 店舗公開可能鍵が指定されていません。
  'EATN0445', // 店舗公開可能鍵の形式が不正です。
  'EATN0451', // 参考価格は6桁以内の半角数値で設定して下さい。
  'EATN0452', // 販売価格は6桁以内の半角数値で設定して下さい。
  'EATN0453', // 販売者コードは100文字以内の半角英数字記号で設定して下さい。
  'EATN0454', // 店舗販売者コードは100文字以内の半角英数字記号で設定して下さい。
  'EATN0479', // すでに存在するリクエストIDです。
  'EATN0480', // 存在しないリクエストIDです。
] as const;

// Atoneの認証エラーコード。
const ATONE_AUTH_ERROR_CODE = [
  'EATN0303', // 決済認証トークンは有効ではありません。
  'EATN0363', //
  'EATN0302', // 決済認証トークンの形式が不正です。
] as const;

// 後払いのSYSTEM_ERROR扱い
const ATOBARAI_SYSTEM_ERROR_CODE = [
  'EPRO0102', // 1000件以下の正常な取引情報が設定されていること。
  'EPRO0104', // 依頼データ内に加盟店取引ID、顧客請求金額が同一となる有効な取引
  'EIMD9999', // NP後払いのサーバが死んでる時とか。
] as const;

// 使用ポイント超過。保有ポイントより多いポイントを使用した場合と、商品価格より多いポイント（払い過ぎ）を使用した時のエラー。
const POINT_ERROR = ['EATN1103', 'EATN0312'] as const;

// オリジン不正。
const INVALID_ORIGIN_ERROR = ['EIMD0004'] as const;

// エンドポイントごとのタイムアウト設定。
const timeouts = [
  {
    // /atone/payment/capture POST
    condition: (url: string, method: string) => url.endsWith('/atone/payment/capture') && method === 'POST',
    get timeout() {
      return Number.parseInt(process.env.VUE_APP_API_TIMEOUT_ATONE_CAPTURE, 10);
    },
  },
  {
    // /atone/payment/lite/capture POST
    condition: (url: string, method: string) => url.endsWith('/atone/payment/lite/capture') && method === 'POST',
    get timeout() {
      return Number.parseInt(process.env.VUE_APP_API_TIMEOUT_ATONE_CAPTURE, 10);
    },
  },
  {
    // /atone/payment/split/capture POST
    condition: (url: string, method: string) => url.endsWith('/atone/payment/split/capture') && method === 'POST',
    get timeout() {
      return Number.parseInt(process.env.VUE_APP_API_TIMEOUT_ATONE_CAPTURE, 10);
    },
  },
  {
    // /atone/member/register POST
    condition: (url: string, method: string) => url.endsWith('/atone/member/register') && method === 'POST',
    get timeout() {
      return Number.parseInt(process.env.VUE_APP_API_TIMEOUT_ATONE_MEMBER_REGISTER, 10);
    },
  },
  {
    // /atone/guest/update POST
    condition: (url: string, method: string) => url.endsWith('/atone/guest/update') && method === 'POST',
    get timeout() {
      return Number.parseInt(process.env.VUE_APP_API_TIMEOUT_ATONE_MEMBER_REGISTER, 10);
    },
  },
  {
    // /atone/guest/register POST
    condition: (url: string, method: string) => url.endsWith('/atone/guest/register') && method === 'POST',
    get timeout() {
      return Number.parseInt(process.env.VUE_APP_API_TIMEOUT_ATONE_MEMBER_REGISTER, 10);
    },
  },
  {
    // /atone/payment/{id}/reauthorize POST
    condition: (url: string, method: string) => /\/atone\/payment\/[^/]+\/reauthorize$/.test(url) && method === 'POST',
    get timeout() {
      return Number.parseInt(process.env.VUE_APP_API_TIMEOUT_ATONE_REAUTHORIZE, 10);
    },
  },
  {
    // /np-atobarai/transaction POST
    condition: (url: string, method: string) => url.endsWith('/np_atobarai/transaction') && method === 'POST',
    get timeout() {
      return Number.parseInt(process.env.VUE_APP_API_TIMEOUT_NP_ATOBARAI_TRANSACTION, 10);
    },
  },
  {
    // デフォルトタイムアウト設定。
    condition: () => true,
    get timeout() {
      return Number.parseInt(process.env.VUE_APP_API_TIMEOUT, 10);
    },
  },
];

const requestInterceptorFactory = (options: {
  origin: Ref<string>;
  isLegacy: Ref<boolean>;
}): Parameters<AxiosInterceptorManager<AxiosRequestConfig>['use']> => [
  (conf) => {
    const { origin, isLegacy } = options;
    const url = conf.url?.replace(/\?.*/, '') || '';
    const method = conf.method?.toUpperCase?.() || '';
    const timeout = conf.timeout || notNil(timeouts.find(({ condition }) => condition(url, method))).timeout;
    return {
      ...conf,
      timeout,
      headers: {
        ...conf.headers,
        ...(origin?.value ? { 'Np-Shop-Client-Origin': origin.value } : {}),
        ...(isLegacy?.value ? { 'Np-Shop-Client-Legacy': 'true' } : {}),
      },
    };
  },
];

export const responseInterceptorFactory = (
  _options?: Record<string, never>,
): Parameters<AxiosInterceptorManager<AxiosResponse>['use']> => [
  (value) => value,
  (error: AxiosError<ErrorResponse>) => {
    if (error.message === 'canceled') {
      // リクエストがキャンセルされた場合はエラーイベントを送付しないで終了。
      return Promise.reject(error);
    }
    const errorConfig = error.config ?? {};
    const service = 'meta' in errorConfig ? getServiceFromMeta(errorConfig.meta) : undefined;
    const url = errorConfig.url ?? error.response?.config?.url ?? '';
    const path = (errorConfig.url ?? error.response?.config?.url ?? '').replace(baseUrl, '').replace(/\?.*/, '');
    const method = (errorConfig.method ?? error.response?.config?.method ?? '').toLowerCase();

    /*----------------------------------------------------------------------*/
    /* ポイント取得でエラーが発生したばあいは-1ポイントとし、かつイベント発火もさせない。 */
    /*----------------------------------------------------------------------*/
    if (path === '/atone/point' && method === 'get') {
      return Promise.resolve(pointGetErrorDummyResponse);
    }

    /*-------------------------------------------------------*/
    /* あらゆるエラーを許容する特別なエンドポイント。                */
    /*-------------------------------------------------------*/
    // エラーエンドポイントは応答がなんであれ完全に無視する。
    if (path === '/atone/javascript/error' && method === 'post') {
      return Promise.resolve(error.response);
    }
    // 郵便番号検索もとにかくエラーにしない。
    if (path === '/atone/zip_address' && method === 'get') {
      return Promise.resolve(error.response);
    }

    /*-------------------------------------------------------*/
    /* システム的に不正なレスポンス値。                           */
    /*-------------------------------------------------------*/
    // TODO: タイムアウトは？
    if (
      errorConfig.url === undefined ||
      error.response?.config.method === undefined ||
      typeof error.response.data.details === 'string'
    ) {
      // 例外なくシステムエラーをブロードキャストする。
      broadcast.send({
        type: 'API_ERROR',
        payload: {
          service,
          url,
          errorType: API_ERROR_TYPE.SYSTEM_ERROR,
          errorResponse: error.response?.data,
          statusCode: error.response?.status,
        },
      });
      return Promise.reject(error);
    }

    const statusCode = error.response.status;
    const hundredCode = Math.floor(statusCode / 100);
    const errorCodes = error.response.data.details.errors.map((err) => err.code);

    /*-----------------*/
    /* オリジンエラー対応 */
    /*-----------------*/
    if (errorCodes.some((code) => includes(...INVALID_ORIGIN_ERROR)(code))) {
      broadcast.send({
        type: 'API_ERROR',
        payload: {
          service,
          errorType: 'SHOP_ERROR',
          url,
          errorResponse: error?.response.data,
          statusCode,
        },
      });
      return Promise.reject(error);
    }

    /*----------------------*/
    /* エンドポイント別特殊対応 */
    /*----------------------*/
    /*---------------------------------------------------------*/
    /* 超特殊ルール。APIのレスポンス不正でデータがおかしいのを修正する。 */
    /*---------------------------------------------------------*/
    if (
      // APIのデータ不備修正。フィールドに対するマッピング情報を追加する。
      // TODO: そのうちbff側で修正すること。こんなのフロントのタスクじゃないし、健全じゃないね。
      ['/atone/member/register', '/atone/member/validate', '/atone/guest/update/validate'].includes(path) &&
      method === 'post'
    ) {
      error.response.data.details.errors.forEach((err) => {
        switch (err.code) {
          case 'EATN0901': {
            err.params.push('address');
            break;
          }
          case 'EATN0902': {
            err.params.push('zip_code');
            break;
          }
          case 'EATN0438': {
            err.params.push('member_information');
            break;
          }
          case 'EATN0444': {
            err.params.push('address_name');
            break;
          }
          default:
        }
      });
    }

    /*----------------------*/
    /* エンドポイント別特殊対応 */
    /*----------------------*/
    if (
      // 例外的に画面で分岐処理させるため、システムエラーから許容するやつ。
      path === '/atone/authenticate' &&
      method === 'get' &&
      errorCodes.some((code) => includes(...ATONE_AUTH_ERROR_CODE)(code))
    ) {
      broadcast.send({
        type: 'API_ERROR',
        payload: {
          service,
          url,
          errorType: API_ERROR_TYPE.BUSINESS_ERROR,
          errorResponse: error.response?.data,
          statusCode,
        },
      });
      return Promise.reject(error);
    }
    if (
      ((/^\/atone\/payment\/[^/]*\/reauthorize$/.test(path) && method === 'put') ||
        (path === '/atone/payment/capture' && method === 'post') ||
        (path === '/atone/payment/lite/capture' && method === 'post')) &&
      errorCodes.some((code) => includes(...ATONE_AUTH_ERROR_CODE)(code))
    ) {
      // 決済時のトークンエラーのみ認証エラーをショップに返却する。画面はシステムエラーを表示する。
      broadcast.send({
        type: 'API_ERROR',
        payload: {
          service,
          url,
          errorType: API_ERROR_TYPE.SYSTEM_ERROR,
          errorResponse: error.response?.data,
          statusCode,
          // 上書きエラーメッセージ。コールバックのmessageに特別に届けるメッセージ。
          // TODO: これerrorResponse内の例外名そのまま突っ込むとかするべきなの？
          name: 'UnauthorizedTokenException',
          message: '決済認証トークンが無効です。',
        },
      });
      return Promise.reject(error);
    }
    if (
      ((/^\/atone\/payment\/[^/]*\/reauthorize$/.test(path) && method === 'put') ||
        (path === '/atone/payment/capture' && method === 'post')) &&
      errorCodes.some((code) => includes(...POINT_ERROR)(code))
    ) {
      // point超過エラー発生。
      broadcast.send({
        type: 'API_ERROR',
        payload: {
          service,
          url,
          errorType: API_ERROR_TYPE.BUSINESS_ERROR,
          errorResponse: error.response?.data,
          statusCode,
        },
      });
      return Promise.reject(error);
    }
    if (
      // 例外的に画面で分岐処理させるため、システムエラーから許容するやつ。
      // authenticatePost
      statusCode === 404 &&
      path === '/atone/authenticate' &&
      method === 'post'
    ) {
      broadcast.send({
        type: 'API_ERROR',
        payload: {
          service,
          url,
          errorType: API_ERROR_TYPE.BUSINESS_ERROR,
          errorResponse: error.response?.data,
          statusCode,
        },
      });
      return Promise.reject(error);
    }
    if (
      // SMS確認は失敗する場合404でも、ビジネスエラーにする。
      // 仮会員から会員への昇格の場合は、/guest/update内でSMS番号が検証される
      statusCode === 404 &&
      [
        '/atone/member/sms/verify',
        '/atone/sms/verify',
        '/atone/guest/sms/authenticate/verify',
        '/atone/guest/update',
      ].includes(path) &&
      method === 'post' &&
      errorCodes.some((code) => ['EATN0310'].includes(code))
    ) {
      broadcast.send({
        type: 'API_ERROR',
        payload: {
          service,
          url,
          errorType: API_ERROR_TYPE.BUSINESS_ERROR,
          errorResponse: error.response?.data,
          statusCode,
        },
      });
      return Promise.reject(error);
    }
    // 登録時のバリデーションAPIに関して、以下でハンドルするものはバリデーションエラー。これはわざわざエラーコールバックもjs/errorも実行しない。
    if (
      ['/atone/member/validate', '/atone/guest/update/validate', '/atone/guest/register/validate'].includes(path) &&
      method === 'post' &&
      errorCodes.some((code) =>
        includes(
          // 会員登録ででる加盟店エラーはバリデーションエラーだけと思われる。
          ...SHOP_ERROR_CODE,
          // 登録済み電話番号など特殊ケース。
          'EATN0435',
          'EATN0474',
        )(code),
      )
    ) {
      broadcast.send({
        type: 'API_ERROR',
        payload: {
          service,
          url,
          errorType: API_ERROR_TYPE.BUSINESS_ERROR,
          errorResponse: error.response?.data,
          statusCode,
        },
      });
      return Promise.reject(error);
    }
    if (
      // fufururu/tokenのpost(np会員idでログイン)では、passwordでEATN0396が出た場合、パスワード不正でビジネスエラーになる。
      path === '/atone/fufururu/token' &&
      method === 'post' &&
      typeof error.response.data.details !== 'string' &&
      error.response.data.details.errors.some(
        (e: AtoneErrorMessage) => e.code === 'EATN0396' && e.params.includes('password'),
      )
    ) {
      broadcast.send({
        type: 'API_ERROR',
        payload: {
          service,
          url,
          errorType: API_ERROR_TYPE.BUSINESS_ERROR,
          errorResponse: error.response.data,
          statusCode,
        },
      });
      return Promise.reject(error);
    }

    /*---------------------------*/
    /* NP後払いのシステムエラー対応。 */
    /*---------------------------*/
    if (path === '/np_atobarai/transaction') {
      // まずシステムエラー。
      if (errorCodes.some((code) => includes(...ATOBARAI_SYSTEM_ERROR_CODE)(code))) {
        broadcast.send({
          type: 'API_ERROR',
          payload: {
            service,
            url,
            errorType: API_ERROR_TYPE.SYSTEM_ERROR,
            errorResponse: error.response?.data,
            statusCode,
          },
        });
        return Promise.reject(error);
      }
      // システムエラーでもメンテナンスエラーでもなければ、ショップエラーとする。
      if (!includes(...errorCodes)('EIMD9901')) {
        broadcast.send({
          type: 'API_ERROR',
          payload: {
            service,
            url,
            errorType: API_ERROR_TYPE.SHOP_ERROR,
            errorResponse: error.response?.data,
            statusCode,
          },
        });
        return Promise.reject(error);
      }
    }

    // //////////////////////////////////
    // // 汎用ルール。
    // //////////////////////////////////
    if (
      // 加盟店エラーにするやつ。
      errorCodes.some((code) => includes(...SHOP_ERROR_CODE)(code))
    ) {
      broadcast.send({
        type: 'API_ERROR',
        payload: {
          service,
          url,
          errorType: API_ERROR_TYPE.SHOP_ERROR,
          errorResponse: error.response?.data,
          statusCode,
        },
      });
      return Promise.reject(error);
    }
    if (
      // メンテナンスにするやつ。
      includes(...errorCodes)('EIMD9901')
    ) {
      broadcast.send({
        type: 'API_ERROR',
        payload: {
          service,
          url,
          errorType: API_ERROR_TYPE.MAINTAINANCE_ERROR,
          errorResponse: error.response?.data,
          statusCode,
        },
      });
      return Promise.reject(error);
    }
    if (
      // システムエラーにするやつ。
      hundredCode === 5 ||
      statusCode === 404 ||
      errorCodes.some((code) => [...ATONE_AUTH_ERROR_CODE, 'EATN0307'].includes(code))
    ) {
      broadcast.send({
        type: 'API_ERROR',
        payload: {
          service,
          url,
          errorType: API_ERROR_TYPE.SYSTEM_ERROR,
          errorResponse: error.response?.data,
          statusCode,
        },
      });
      return Promise.reject(error);
    }
    // その他はビジネスエラー。
    broadcast.send({
      type: 'API_ERROR',
      payload: {
        service,
        url,
        errorType: API_ERROR_TYPE.BUSINESS_ERROR,
        errorResponse: error.response?.data,
        statusCode,
      },
    });
    return Promise.reject(error);
  },
];

/**
 * Bffと通信するためのモジュールを提供するcomposable
 *
 * @param alternateAdapter デフォルトと異なるaxiosAdapterを設定したい場合。主にテストコードでbffに対するリクエストをインターセプトする目的で使用しhます。
 */
export const useBerlinBff = (alternateAdapter?: AxiosAdapter) => {
  const origin = useOrigin();
  const [isLegacy] = useIsLegacy();

  const defaultTransformResponses = [axios.defaults?.transformResponse ?? []].flat();
  const defaultTransformRequests = [axios.defaults?.transformRequest ?? []].flat();
  const atoneAxiosInstance = axios.create({
    adapter: alternateAdapter ?? adapter,
    transformResponse: [
      ...defaultTransformResponses,
      (data, headers): unknown => {
        if (
          headers !== null &&
          typeof headers === 'object' &&
          headers['content-type'] &&
          typeof headers['content-type'] === 'string' &&
          headers['content-type'].indexOf('application/json') !== -1 &&
          data
        ) {
          return camelCaseDeep(data);
        }
        return data;
      },
    ],

    transformRequest: [
      (data, _headers): unknown => {
        if (typeof data !== 'string') {
          return data;
        }
        return JSON.stringify(snakeCaseDeep(JSON.parse(data)));
      },
      ...defaultTransformRequests,
    ],

    paramsSerializer(params) {
      return qs.stringify(snakeCaseDeep(params));
    },
  });
  atoneAxiosInstance.interceptors.request.use(...requestInterceptorFactory({ origin, isLegacy }));
  atoneAxiosInstance.interceptors.response.use(...responseInterceptorFactory());

  return {
    authenticate: AtoneAuthenticateApiFactory(undefined, baseUrl, atoneAxiosInstance),
    member: AtoneMemberApiFactory(undefined, baseUrl, atoneAxiosInstance),
    zipAddress: AtoneZipAddressApiFactory(undefined, baseUrl, atoneAxiosInstance),
    sms: AtoneSmsApiFactory(undefined, baseUrl, atoneAxiosInstance),
    js: AtoneJavascriptApiFactory(undefined, baseUrl, atoneAxiosInstance),
    payment: AtonePaymentApiFactory(undefined, baseUrl, atoneAxiosInstance),
    tracks: AtoneTracksApiFactory(undefined, baseUrl, atoneAxiosInstance),
    shop: AtoneShopApiFactory(undefined, baseUrl, atoneAxiosInstance),
    requestToken: AtoneRequestTokenApiFactory(undefined, baseUrl, atoneAxiosInstance),
    point: AtonePointApiFactory(undefined, baseUrl, atoneAxiosInstance),
    fufururu: AtoneFufururuApiFactory(undefined, baseUrl, atoneAxiosInstance),
    npAtobaraiTransaction: NpAtobaraiTransactionApiFactory(undefined, baseUrl, atoneAxiosInstance),
    napoleon: NapoleonApiFactory(undefined, baseUrl, atoneAxiosInstance),
  } as const;
};

type BerlinBffType = ReturnType<typeof useBerlinBff>;

export const BerlinBffContext = createContext<BerlinBffType>(useBerlinBff);

/**
 * エラーレスポンス判定。
 * @param value
 * @returns
 */
export const isErrorResponse = (value: unknown): value is ErrorResponse =>
  hasProps(value, 'systemType', isStr) &&
  hasProps(value, 'path', isStr) &&
  hasProps(value, 'timestamp', isStr) &&
  hasProps(value, 'statusCode', isNum) &&
  hasPath(value, 'details') &&
  (Object.values(ErrorResponseSystemTypeEnum) as string[]).includes(value.systemType);
